import * as React from 'react';
import { useContext, useState } from 'react';
import { ApiVersion, VegobjekttypeState } from '@/state';
import { ConfigAndDataContext } from '@/state/context/ConfigAndDataContext';
import Toast from '../../objects/Toast';
import ExportSettingsDialog from '@/components/avgrensning/shared/ExportSettingsDialog';
import {
    buildAreaParams,
    buildCsvLink,
    buildSosiLink,
    buildUrlParams,
    isSensitive,
} from '@/utils/exportUtils';
import { useSelector } from 'react-redux';
import {
    stateMap,
    stateOmrader,
    statePreferences,
    stateSearchDropdownState,
    stateUser,
    stateVegnett,
} from '@/selectors/selectors';
import { download, getApiVersion } from '@/utils/Utils';
import { getBlob } from '@/server/server';

interface DownloadAllResultsBoxProps {
    vegobjekttypeStates: VegobjekttypeState[];
}

export const DownloadAllResultsBox = ({
    vegobjekttypeStates,
}: DownloadAllResultsBoxProps) => {
    const context = useContext(ConfigAndDataContext);

    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });

    const preferences = useSelector(statePreferences);
    const omraderState = useSelector(stateOmrader);
    const mapState = useSelector(stateMap);
    const vegnettState = useSelector(stateVegnett);
    const searchDropdownState = useSelector(stateSearchDropdownState);
    const userState = useSelector(stateUser);

    const requireTypeId = (votState: VegobjekttypeState): number => {
        if (votState.typeId === null) {
            throw new Error('Kan ikke eksportere vegobjekttype uten type-ID');
        }
        return votState.typeId;
    };

    const getUrlParamsForVot = (votState: VegobjekttypeState): string => {
        const votQueries = votState
            .toQueries()
            .withTidspunkt(searchDropdownState.timestamp);

        const params = {
            ...votQueries.toParams(),
            ...mapState.toQueries().toParams(),
            ...omraderState.toOmradeQueries(undefined).toParams(),
            ...vegnettState.toQueries().toParams(),
            ...preferences.toExportQueries().toParams(),
        };

        if (
            !!params.fylke ||
            !!params.kommune ||
            !!params.kontraktsomrade ||
            !!params.riksvegrute
        ) {
            params.kartutsnitt = buildAreaParams(omraderState);
        }

        return buildUrlParams(params);
    };

    const { restapi_url_v3, restapi_url, eksport_url, eksport_x_url } = context.config;
    const apiVersion = getApiVersion();
    const apiUrl = apiVersion === ApiVersion.V3 ? restapi_url_v3 : restapi_url;
    const eksportUrl =
        apiVersion === ApiVersion.V4 && eksport_x_url ? eksport_x_url : eksport_url;
    const eksportApiUrl =
        apiVersion === ApiVersion.V4 && eksport_x_url ? undefined : apiUrl;

    const csvLink = (votState: VegobjekttypeState) =>
        buildCsvLink({
            typeId: requireTypeId(votState),
            baseUrlParams: getUrlParamsForVot(votState),
            eksportUrl: eksportUrl,
            apiUrl: eksportApiUrl,
        });

    const sosiLink = (votState: VegobjekttypeState) =>
        buildSosiLink({
            typeId: requireTypeId(votState),
            baseUrlParams: getUrlParamsForVot(votState),
            eksportUrl: eksportUrl,
            apiUrl: eksportApiUrl,
            exportEnumFormat: preferences.exportEnumFormat,
            omraderState,
        });

    const handleDownloadAll = async (
        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        format: 'csv' | 'sosi'
    ) => {
        e.preventDefault();
        setDownloading(true);
        setDownloadProgress({ current: 0, total: vegobjekttypeStates.length });

        for (let i = 0; i < vegobjekttypeStates.length; i++) {
            const votState = vegobjekttypeStates[i];
            const typeId = requireTypeId(votState);
            const url = format === 'csv' ? csvLink(votState) : sosiLink(votState);
            const isSensitiveAvailable =
                userState.isLoggedIn && isSensitive(typeId, context);

            const roadObjectTypeName =
                context.data.datakatalog.vegobjekttyper.find(type => type.id === typeId)
                    ?.sosinvdbnavn ?? 'vegobjekt';
            const sensitivityLabel = isSensitiveAvailable ? '-sensitiv' : '';
            const fileType = format === 'csv' ? 'csv' : 'sos';
            const fileName = `${roadObjectTypeName}${sensitivityLabel}-eksport.${fileType}`;

            try {
                const blob = await getBlob(url, isSensitiveAvailable);
                const objectURL = URL.createObjectURL(blob);
                download(objectURL, fileName);
                URL.revokeObjectURL(objectURL);
            } catch (error) {
                console.error(`Failed to download ${roadObjectTypeName}:`, error);
            }

            setDownloadProgress({ current: i + 1, total: vegobjekttypeStates.length });
        }

        setDownloading(false);
    };

    const hasSensitiveData = vegobjekttypeStates.some(
        vot => userState.isLoggedIn && isSensitive(requireTypeId(vot), context)
    );

    const csvTooltip = () => {
        return `Last ned vegobjekter (${vegobjekttypeStates.length} vegobjekttyper) som CSV\nKartprojeksjon: ${preferences.exportProjection}`;
    };

    const sosiTooltip = () => {
        const enumFormat = preferences.exportEnumFormat || 'Ikke satt';
        return `Last ned vegobjekter (${vegobjekttypeStates.length} vegobjekttyper) som SOSI\nKartprojeksjon: ${preferences.exportProjection}\nTillatte verdier: ${enumFormat}`;
    };

    const toastText =
        downloadProgress.total > 0
            ? `Laster ned ${downloadProgress.current}/${downloadProgress.total}...`
            : 'Nedlasting pågår';

    return (
        <div className="c-results-summary c-results-summary--download-all t-hidden-mobile">
            <div className="c-results-summary__export c-results-summary-export">
                <div className="c-results-summary-export__links c-results-summary-export-links t-clearfix">
                    <i
                        className="o-icon-download c-results-summary__icon-download"
                        aria-hidden={true}
                    />
                    <span className="c-results-summary__text">
                        Last ned vegobjekter ({vegobjekttypeStates.length} typer):
                    </span>
                    <a
                        key="csv-link-all"
                        className="c-results-summary-export-links__csv"
                        href="#"
                        onClick={e => handleDownloadAll(e, 'csv')}
                        title={csvTooltip()}
                    >
                        CSV{' '}
                        {hasSensitiveData && (
                            <i className="o-icon-logout c-results-summary__icon-lock" />
                        )}
                    </a>
                    <a
                        key="sosi-link-all"
                        className="c-results-summary-export-links__sosi"
                        href="#"
                        onClick={e => handleDownloadAll(e, 'sosi')}
                        title={sosiTooltip()}
                    >
                        SOSI{' '}
                        {hasSensitiveData && (
                            <i className="o-icon-logout c-results-summary__icon-lock" />
                        )}
                    </a>
                    <button
                        title="Åpne innstillinger for eksport"
                        id="instillinger-link-all"
                        key="instillinger-link-all"
                        className="c-results-summary-export-dialog"
                        onClick={() => {
                            setExportDialogOpen(true);
                        }}
                    >
                        <i
                            className="material-icons"
                            aria-hidden="true"
                            style={{ color: exportDialogOpen ? 'orange' : 'inherit' }}
                        >
                            settings
                        </i>
                    </button>
                </div>
            </div>
            <Toast text={toastText} show={downloading} />
            <ExportSettingsDialog
                isOpen={exportDialogOpen}
                setIsOpen={setExportDialogOpen}
            />
        </div>
    );
};
